import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { CircularProgress, Box } from "@mui/material";

const getLandingPathForRole = (role) => {
  const normalizedRole = String(role || "").toLowerCase();

  switch (normalizedRole) {
    case "control":
      return "/device-controls";
    case "technician":
      return "/my-services";
    case "customer":
      return "/driftsdashboard";
    case "admin":
    case "user":
    default:
      return "/";
  }
};

const getLoginRedirectPath = (location) => {
  const nextPath = `${location.pathname}${location.search}${location.hash}`;
  return `/login?next=${encodeURIComponent(nextPath)}`;
};

/**
 * ProtectedRoute component for role-based access control
 * @param {React.ReactNode} children - The component to render if authorized
 * @param {string[]} allowedRoles - Array of roles that can access this route
 * @param {string} redirectTo - Deprecated; use configured landing page instead
 * @param {boolean} requireAuth - Whether authentication is required (default: true)
 */
const ProtectedRoute = ({
  children,
  allowedRoles = [],
  redirectTo, // deprecated; kept for compatibility but ignored
  requireAuth = true,
}) => {
  const { isAuth, loading, user } = useAuth();
  const location = useLocation();

  // Show spinner while we don't know auth status yet
  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Check authentication first
  if (requireAuth && !isAuth) {
    return <Navigate to={getLoginRedirectPath(location)} replace />;
  }

  // If no specific roles are required, just check authentication
  if (allowedRoles.length === 0) {
    return children;
  }

  // Check if user has required role
  if (!user || !user.role) {
    return <Navigate to={getLoginRedirectPath(location)} replace />;
  }

  const userRole = user.role.toLowerCase();
  const hasRequiredRole = allowedRoles
    .map((role) => role.toLowerCase())
    .includes(userRole);

  if (!hasRequiredRole) {
    return <Navigate to={getLandingPathForRole(user.role)} replace />;
  }

  return children;
};

export default ProtectedRoute;
